import { Field, useFormikContext } from 'formik'
import * as React from 'react'
import { ChangeEvent, useCallback, useEffect } from 'react'
import { IFormikUserModel, UpdateUserFormProps } from './typings'
import { MuiSwitchFormik } from '@user/components/Formik'
import {
  InfoTooltip,
  LightTooltip,
  TidalTextFormikField,
} from '@sales-center/components'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { getStyles } from './styling'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import { useTheme } from '@mui/material/styles'
import useGetUserStatusToggleMsg from '@user/components/Settings/Users/hooks/useGetUserStatusToggleMsg'
import {
  inactiveUserUtilize,
  unmappedEmployeeTimeZone,
} from '@user/components/Settings/Users/UpdateUser/constants'
import { useSelector } from 'react-redux'
import { selectCurrentUser } from '@user/store/authorization'
import { userHasOpenRecords } from '../UsersNew/functions'

export const UpdateUnmappedUserFormNew: React.FC<UpdateUserFormProps> = (
  props: UpdateUserFormProps,
) => {
  const {
    setFieldValue,
    values: { isActive },
    setValues,
  } = useFormikContext<IFormikUserModel>()
  const {
    user,
    setReAssignDrawerOpen,
    initialActive,
    openReAssignDrawerAfterSave,
  } = props

  const activeMessage = useGetUserStatusToggleMsg(user, user?.emailIsUnique)
  const currentUser = useSelector(selectCurrentUser)

  const hasOpenRecords = user ? userHasOpenRecords(user) : false

  const onChangeStatus = useCallback(() => {
    if (hasOpenRecords && !openReAssignDrawerAfterSave) {
      setReAssignDrawerOpen(true)
    } else {
      setFieldValue('isActive', !isActive)
    }
  }, [
    activeMessage,
    openReAssignDrawerAfterSave,
    setReAssignDrawerOpen,
    setFieldValue,
    isActive,
    hasOpenRecords,
  ])

  // ========== H O O K S ==========
  useEffect(() => {
    if (user) {
      setValues((v) => ({
        ...v,
        firstName: user.firstName,
        lastName: user.lastName,
        emailAddress: user.emailAddress,
        id: user.id,
        isActive: user.isActive || initialActive,
        emailUnique: Boolean(user.emailIsUnique),
      }))
    }
  }, [
    user?.firstName,
    user?.lastName,
    user?.emailAddress,
    user?.id,
    user?.isActive,
    user?.emailIsUnique,
    initialActive,
    setValues,
  ])

  const styles = getStyles(useTheme())
  return (
    <>
      <Typography variant="h7">User Details</Typography>
      <Stack gap={1.2} mt={1.2}>
        <TidalTextFormikField
          name="firstName"
          label="First Name"
          required
          disabled={!isActive}
          avoidValidateOnBlur
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            setFieldValue('firstName', event.target.value.trimStart())
          }
        />
        <TidalTextFormikField
          name="lastName"
          label="Last Name"
          required
          disabled={!isActive}
          avoidValidateOnBlur
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            setFieldValue('lastName', event.target.value.trimStart())
          }
        />
        <TidalTextFormikField
          name="emailAddress"
          label="Email Address"
          required
          disabled={!isActive}
          avoidValidateOnBlur
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            setFieldValue('emailAddress', event.target.value.trimStart())
          }
        />
      </Stack>

      {props?.user && (
        <>
          <Box mt={3.2} alignItems={'center'}>
            <Typography variant="h7">Status</Typography>
            <InfoTooltip
              infoText={inactiveUserUtilize}
              tooltipPlacement="right"
              sx={{
                ...styles.infoActiveIcon,
                color: (theme) => theme.palette.action.active,
                ml: 0.5,
              }}
            />
          </Box>
          <Box ml={1} alignItems={'center'}>
            <Field
              name="isActive"
              component={MuiSwitchFormik}
              disabled={
                currentUser.userId === user?.id ||
                user?.isSuperAdmin ||
                (activeMessage !== undefined && !hasOpenRecords)
              }
              label={isActive ? 'Active' : 'Inactive'}
              onChange={onChangeStatus}
            />
            {activeMessage && (
              <InfoTooltip
                infoText={activeMessage}
                tooltipPlacement="right"
                sx={{
                  ...styles.infoActiveIcon,
                  color: (theme) => theme.palette.action.active,
                  mb: 0.7,
                  ml: -1,
                }}
              />
            )}
          </Box>
        </>
      )}
      {/* ========== T I M E   Z O N E ========== */}
      <Box mt={2}>
        <Typography variant="h7">Employee Details</Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2.3,
            mt: 0.5,
          }}
        >
          <Typography variant="subtitle2">Time Zone</Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.6,
            }}
          >
            <Typography variant="body2">UTC-5:00 (Eastern Time)</Typography>
            <LightTooltip title={unmappedEmployeeTimeZone}>
              <InfoOutlinedIcon
                sx={styles.infoIcon}
                data-test-id="unmappedUserTimeZoneInfoIcon"
              />
            </LightTooltip>
          </Box>
        </Box>
      </Box>
    </>
  )
}
